import React, { useMemo } from "react"

import { useAgentsList } from "@/agents/catalog"
import { ENTITY_ICONS } from "@/constants/entity-icons"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useConversationsFilter } from "../../contexts/ConversationsFilterContext"
import type { ConversationsFilterSectionProps } from "../../types/filters"

const AgentIcon = ENTITY_ICONS.agents

/**
 * Agent multi-select section of the Conversations filter menu.
 */
export const AgentFilterSection: React.FC<ConversationsFilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled = true,
}) => {
  const {
    state: { agentIds },
    setAgentIds,
  } = useConversationsFilter()
  const {
    data: agents,
    isError,
    isLoading,
  } = useAgentsList({
    enabled: filterEnabled,
  })

  const items = useMemo(
    () =>
      (agents ?? []).map((agent) => ({
        id: agent.id,
        label: agent.display_name,
        searchValue: [
          agent.display_name,
          agent.customer_agent_id,
          agent.phone_number,
        ]
          .filter(Boolean)
          .join(" "),
      })),
    [agents],
  )

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: agentIds.length,
    isDeferred: !filterEnabled,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      width="w-72"
      disabled={isDisabled}
      emptyMessage="No agents available in this workspace."
      trigger={
        <FilterTrigger
          label="Agents"
          icon={<AgentIcon className="w-4 h-4" />}
          isActive={agentIds.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={agentIds}
        onSelectionChange={setAgentIds}
        isLoading={isLoading}
        isDeferred={!filterEnabled}
        isOpen={isOpen}
        placeholder="Search agents..."
        emptyMessage="No agents found"
        noItemsMessage="No agents available"
        width="w-72"
        renderItem={(item) => (
          <span className="block min-w-0 truncate text-sm">{item.label}</span>
        )}
      />
    </FilterPopover>
  )
}
